
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent, 
  ChartLegend, 
  ChartLegendContent 
} from "@/components/ui/chart";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer
} from "recharts";
import { SystemMetric } from "@/pages/tech/SystemMonitor";

interface PerformanceChartProps {
  data: SystemMetric[];
  isLoading: boolean;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({ data, isLoading }) => {
  console.log("PerformanceChart - Received data:", data);

  // Set up chart colors for our metrics
  const chartConfig = {
    cpu: { label: "CPU", color: "#004161" },
    memory: { label: "Memory", color: "#0f8f8c" },
    requests: { label: "Requests", color: "#cfb9a2" }
  };

  // Display a loading state while fetching data
  if (isLoading) {
    console.log("PerformanceChart - Showing loading state");
    return (
      <Card className="bg-white">
        <CardHeader className="pb-0">
          <CardTitle className="text-xl font-title text-primary">System Performance (24 Hours)</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Display a message if no data is available
  if (!data || data.length === 0) {
    console.log("PerformanceChart - No data available");
    return (
      <Card className="bg-white">
        <CardHeader className="pb-0">
          <CardTitle className="text-xl font-title text-primary">System Performance (24 Hours)</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 flex items-center justify-center h-[350px]">
          <p className="text-muted-foreground text-center">No performance data available. Please check database connectivity or ensure metrics are being collected.</p>
        </CardContent>
      </Card>
    );
  }

  console.log("PerformanceChart - Rendering with data:", data);
  return (
    <Card className="bg-white">
      <CardHeader className="pb-0">
        <CardTitle className="text-xl font-title text-primary">
          System Performance (24 Hours)
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="h-[350px]">
          <ChartContainer 
            config={chartConfig}
            className="h-full w-full"
          >
            <LineChart 
              data={data} 
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1eceb" />
              <XAxis 
                dataKey="metric_date" 
                stroke="#6c6c6c" 
              />
              <YAxis 
                stroke="#6c6c6c"
                width={60}
                orientation="left"
                yAxisId={0}
                domain={['auto', 'auto']}
                padding={{ top: 20, bottom: 20 }}
                allowDecimals={true}
                allowDataOverflow={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Line 
                type="monotone" 
                dataKey="cpu_usage" 
                name="cpu" 
                stroke="#004161" 
                strokeWidth={2} 
                dot={false}
                isAnimationActive={true}
                animationDuration={1000}
              />
              <Line 
                type="monotone" 
                dataKey="memory_usage" 
                name="memory" 
                stroke="#0f8f8c" 
                strokeWidth={2} 
                dot={false}
                isAnimationActive={true}
                animationDuration={1000}
              />
              <Line 
                type="monotone" 
                dataKey="request_count" 
                name="requests" 
                stroke="#cfb9a2" 
                strokeWidth={2} 
                dot={false}
                isAnimationActive={true}
                animationDuration={1000}
              />
            </LineChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default PerformanceChart;
